import { memo, useCallback, useState } from 'react';

import { PageTitle } from '@dec/shared';
import GridApplication from 'app/components/grid/gridApplication';
import { catalogs } from 'app/modules/Parameters/Catalog/Catalog/services/catalog';
import { useCatalogList } from 'app/modules/Parameters/Catalog/Catalog/services/useCatalog';
import * as Icon from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type PaymentFilter, type PaymentSummary } from '../../models/Payment';
import { BaseUrPayment } from '../../services/payment.service';

import { getPaymentColumns } from './columns';
import { PaymentFilters } from './filters';
import { mapPaymentFormToSearch, type PaymentFilterForm } from './search';

interface ViewPaymentsProps {
  titleKey?: string;
}

export const ViewPaymentsPage = memo(({ titleKey }: ViewPaymentsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [search, setSearch] = useState<PaymentFilter>({});

  /*
   * Catálogo de recaudadores.
   */
  const [{ data: taxCollectors = [], loading: loadingTaxCollectors, error: errorTaxCollectors }] =
    useCatalogList(catalogs.taxCollector);

  /*
   * Navegación al detalle.
   *
   * La navegación queda en la página porque useNavigate
   * es un hook. columns.ts solamente recibe el callback.
   */
  const handleViewPayment = useCallback(
    (payment: PaymentSummary) => {
      navigate('/payments/declaration/view/' + payment.id);
    },
    [navigate]
  );

  /*
   * Columnas del grid.
   */
  const columns = getPaymentColumns(handleViewPayment);

  /*
   * Nueva búsqueda.
   */
  const handleSearch = useCallback((values: PaymentFilterForm) => {
    console.log({ values: values });
    setSearch(mapPaymentFormToSearch(values));
  }, []);

  /*
   * Limpiar búsqueda.
   *
   * filter.tsx se encarga de limpiar visualmente
   * el formulario.
   *
   * La página limpia el objeto enviado al grid.
   */
  const handleClear = useCallback(() => {
    setSearch({});
  }, []);

  /*
   * Estados de carga/error.
   */
  const loading = loadingTaxCollectors;

  const hasError = !!errorTaxCollectors;

  return (
    <>
      <PageTitle title={t(titleKey ?? 'taxpayer-payments.title.page')} icon={Icon.Wallet2} />

      {loading && <div>{t('common.loading')}</div>}

      {hasError && <div>{t('common.error')}</div>}

      {!loading && !hasError && (
        <>
          <PaymentFilters
            taxCollectors={taxCollectors}
            onSearch={handleSearch}
            onClear={handleClear}
          />

          <GridApplication url={BaseUrPayment} columnDefs={columns} search={search} />
        </>
      )}
    </>
  );
});

ViewPaymentsPage.displayName = 'ViewPayments';
